import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Toaster } from '@/components/ui/sonner';
import useAnalytics from '@/hooks/useAnalytics';
import '@/App.css';

// Pages
import Home from '@/pages/Home';
import HowItWorks from '@/pages/HowItWorks';
import Pricing from '@/pages/Pricing';
import FAQs from '@/pages/FAQs';
import Terms from '@/pages/Terms';
import TermsAndConditions from '@/pages/legal/TermsAndConditions';
import Complaints from '@/pages/Complaints';
import Contact from '@/pages/Contact';
import OrderFlow from '@/pages/OrderFlow';
import OrderConfirmation from '@/pages/OrderConfirmation';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminGallery from '@/pages/AdminGallery';
import SEOSettings from '@/pages/SEOSettings';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Analytics from '@/pages/Analytics';
import Gallery from '@/pages/Gallery';
import HpdDrawGallery from '@/pages/HpdDrawGallery';
import SketchToCADConversionUK from '@/pages/SketchToCADConversionUK';
import CopyrightAndTakedown from '@/pages/legal/CopyrightAndTakedown';
import RefundsAndCancellation from '@/pages/legal/RefundsAndCancellation';

// SEO Landing Pages
import EstateAgentFloorPlans from '@/pages/EstateAgentFloorPlans';
import RightmoveFloorPlanService from '@/pages/RightmoveFloorPlanService';
import LandlordFloorPlans from '@/pages/LandlordFloorPlans';
import PropertyMarketingFloorPlans from '@/pages/PropertyMarketingFloorPlans';
import EPCFloorPlans from '@/pages/EPCFloorPlans';
import FloorPlanFromSketch from '@/pages/FloorPlanFromSketch';

// Guides — small, high-quality content cluster supporting the DIY tools
import HowToDrawFloorPlanFromMeasurements from '@/pages/guides/HowToDrawFloorPlanFromMeasurements';
import HowToMeasureFloorPlanOnline from '@/pages/guides/HowToMeasureFloorPlanOnline';
import HowToCreateEstateAgentFloorPlan from '@/pages/guides/HowToCreateEstateAgentFloorPlan';

// Plan Measure tool (heavy — code-split to keep main bundle small)
const PlanMeasure = lazy(() => import('@/pages/PlanMeasure'));
const MeasureToolPaywall = lazy(() => import('@/components/MeasureToolPaywall'));

const PlanMeasureLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-slate-100">
    <div className="text-sm text-slate-500">Loading measurement tool…</div>
  </div>
);

const paypalOptions = {
  clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: 'GBP',
  intent: 'capture'
};

// Preserve query string on 301-style redirects so utm_* params aren't lost.
const RedirectWithQuery = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

function AppContent() {
  // Track analytics on every route change
  useAnalytics();

  return (
    <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/order" element={<OrderFlow />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/hpd-draw-gallery" element={<HpdDrawGallery />} />
            <Route path="/sketch-to-cad-conversion-uk" element={<SketchToCADConversionUK />} />

            {/* Legal pages — canonical /legal/* paths */}
            <Route path="/legal/terms" element={<TermsAndConditions />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/complaints" element={<Complaints />} />
            <Route path="/legal/copyright-and-takedown" element={<CopyrightAndTakedown />} />
            <Route path="/legal/refunds-and-cancellation" element={<RefundsAndCancellation />} />

            {/* 301-style redirects from old URLs (preserves query strings) */}
            <Route path="/terms" element={<RedirectWithQuery to="/legal/terms" />} />
            <Route path="/privacy-policy" element={<RedirectWithQuery to="/legal/privacy" />} />
            <Route path="/complaints" element={<RedirectWithQuery to="/legal/complaints" />} />

            {/* Plan Measure marketing route — currently mirrors the embedded tool */}
            <Route path="/plan-measure" element={<RedirectWithQuery to="/measurement-tool" />} />
            <Route path="/floor-plan-service" element={<RedirectWithQuery to="/order" />} />

            {/* SEO Landing Pages */}
            <Route path="/estate-agent-floor-plans" element={<EstateAgentFloorPlans />} />
            <Route path="/rightmove-floor-plan-service" element={<RightmoveFloorPlanService />} />
            <Route path="/landlord-floor-plans" element={<LandlordFloorPlans />} />
            <Route path="/property-marketing-floor-plans" element={<PropertyMarketingFloorPlans />} />
            <Route path="/epc-floor-plans" element={<EPCFloorPlans />} />
            <Route path="/floor-plan-from-sketch" element={<FloorPlanFromSketch />} />

            {/* Guides — small, high-quality content cluster supporting the DIY tools */}
            <Route path="/guides/how-to-draw-a-floor-plan-from-measurements" element={<HowToDrawFloorPlanFromMeasurements />} />
            <Route path="/guides/how-to-measure-a-floor-plan-online" element={<HowToMeasureFloorPlanOnline />} />
            <Route path="/guides/how-to-create-an-estate-agent-floor-plan" element={<HowToCreateEstateAgentFloorPlan />} />

            {/* Paid browser tools — lazy loaded to keep main bundle small */}
            <Route
              path="/measurement-tool"
              element={
                <Suspense fallback={<PlanMeasureLoader />}>
                  <MeasureToolPaywall renderChild={(access) => <PlanMeasure access={access} initialWorkspace="measure" />} />
                </Suspense>
              }
            />

            {/* HPD Draw — primary crawlable Floor Plan Creator entry route (same app, opens in Create mode) */}
            <Route
              path="/floor-plan-creator"
              element={
                <Suspense fallback={<PlanMeasureLoader />}>
                  <MeasureToolPaywall renderChild={(access) => <PlanMeasure access={access} initialWorkspace="floorplan" />} />
                </Suspense>
              }
            />

            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/gallery" element={<AdminGallery />} />
            <Route path="/admin/seo" element={<SEOSettings />} />
            <Route path="/admin/analytics" element={<Analytics />} />
    </Routes>
  );
}

function App() {
  return (
    <PayPalScriptProvider options={paypalOptions}>
      <BrowserRouter>
        <div className="App">
          <AppContent />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </PayPalScriptProvider>
  );
}

export default App;
